import { useEffect, useState } from "react";
import { Input, Label, Button, Spinner } from "reactstrap";
import DateRangePicker from "components/DateRangePicker";
import { getMcapSchoolOptions, getMcapExportUrl } from "api/api.service";
import { clearCache, getCachedAt, mcapCacheKey } from "utils/mcapCache";
import SchoolPicker from "./SchoolPicker";
import typography from "../typography";

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function McapFilters({
  range,
  onRangeChange,
  onRangeClear,
  hasUserSelectedDate,
  schoolId,
  sectionId,
  onSchoolChange,
  onSectionChange,
  exportMetric,
  exportParams = {},
  showSchoolPicker = true,
}) {
  const [schools, setSchools] = useState([]);
  // Read once on render: the entry is written by the tab's own fetch, so by the
  // time the filter bar paints, the value is there.
  const cachedAt = exportMetric ? getCachedAt(mcapCacheKey(exportMetric, exportParams)) : null;
  const asOf = cachedAt ? new Date(cachedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!showSchoolPicker) return;
    getMcapSchoolOptions()
      .then((res) => setSchools(res?.schools || []))
      .catch(() => setSchools([]));
  }, [showSchoolPicker]);

  const selectedSchool = schools.find((s) => String(s.id) === String(schoolId));
  const sectionsForSchool = selectedSchool?.sections || [];

  const handleSchoolChange = (nextSchoolId) => {
    onSchoolChange(nextSchoolId);
    onSectionChange(""); // a section only makes sense within the school it was picked from
  };

  // Drops every cached MCAP payload and reloads, so each tab refetches. A
  // reload alone would not do it: sessionStorage survives refresh and only
  // clears when the tab closes, which is exactly why stale figures persisted.
  const handleRefresh = () => {
    clearCache();
    window.location.reload();
  };

  const handleExport = async () => {
    if (!exportMetric) return;
    setExporting(true);
    try {
      const blob = await getMcapExportUrl({ metric: exportMetric, ...exportParams });
      downloadBlob(blob, `mcap_${exportMetric}_${new Date().toISOString().slice(0, 10)}.csv`);
    } catch {
      // Export failing shouldn't block the rest of the dashboard — the
      // global fetch error handler already surfaces a toast.
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mcap-filters-con my-3">
      <div className="mcap-date-range-con">
        {/* Dropped in the compact header (Overview): the input's own
            placeholder already says "Select Date Range", and the label's
            extra height above it was what threw off vertically centering
            the heading beside this bar — the other, fuller filter bars keep
            it, matching School/Section's own labels above them. */}
        {showSchoolPicker && <Label className="form-label mb-0">{typography.DATE_RANGE}</Label>}
        <DateRangePicker
          range={range}
          onChange={onRangeChange}
          onClear={onRangeClear}
          showClearIcon
          hasUserSelectedDate={hasUserSelectedDate}
        />
      </div>

      {showSchoolPicker && (
        <>
          {/* A ranked, searchable picker rather than an alphabetical <select>.
              The old list put "#DEMO Ease Sandbox" above every real school and
              gave no clue which of twenty-two names was worth opening. Absent
              entirely on Overview — see useMcapFilters' own comment on why. */}
          <div className="mcap-org-filter-con">
            <SchoolPicker value={schoolId} onChange={handleSchoolChange} filters={exportParams} />
          </div>

          <div className="mcap-org-filter-con">
            <Label className="form-label mb-0">{typography.SECTION}</Label>
            <Input
              type="select"
              bsSize="sm"
              value={sectionId || ""}
              onChange={(e) => onSectionChange(e.target.value)}
              disabled={!schoolId}
            >
              <option value="">{typography.ALL_SECTIONS}</option>
              {sectionsForSchool.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Input>
          </div>
        </>
      )}

      {/* Says how old the figures are, and lets anyone force a refetch.
          Without this the cache is invisible: an administrator could read
          numbers up to a TTL out of date with nothing on screen to suggest it,
          which after a deploy looks exactly like a release that did not work. */}
      <div className="mcap-freshness">
        <button type="button" className="mcap-refresh-btn" onClick={handleRefresh} title={typography.REFRESH_HINT}>
          <i className="mdi mdi-refresh" aria-hidden="true" />
          {typography.REFRESH}
        </button>
        <span className="mcap-asof">
          {asOf ? `${typography.AS_OF} ${asOf}` : typography.AS_OF_LIVE}
        </span>
      </div>

      {exportMetric && (
        <Button color="light" size="sm" className="mcap-export-btn" onClick={handleExport} disabled={exporting}>
          {exporting ? <Spinner size="sm" /> : typography.EXPORT_CSV}
        </Button>
      )}
    </div>
  );
}
